/**
 * Hear Control — session helpers (mock + Cognito-pattern JWT when API configured).
 * Never log PII, tokens, or clinical content.
 */

import {
  CONFIG,
  MOCK_TOKEN_EXPIRY_MS,
  STORAGE_KEYS,
  isApiConfigured,
} from "/assets/js/config.js";
import {
  createEnsureFreshSession,
} from "/assets/js/auth-session-refresh.js";
import { resolveSessionLanding } from "/assets/js/permission_control/session-landing.js";
import { getApiBaseUrl } from "/assets/js/utils/company-context.js";
import { fetchWithRetry } from "/assets/js/utils/fetch-retry.js";
import { FIXTURE_KEYS, MOCK_STORAGE_PREFIX } from "/assets/js/fixtures/constants.js";

const ALL_SESSION_KEYS = Object.values(STORAGE_KEYS);
/** @type {Set<AbortController>} */
const inFlightControllers = new Set();

/** One-shot login e-mail after invite accept. sessionStorage only — not in STORAGE_KEYS. */
export const HEAR_LOGIN_EMAIL_KEY = "hear_login_email";

/**
 * @returns {boolean}
 */
export function isAuthenticated() {
  const loggedIn = localStorage.getItem(STORAGE_KEYS.LOGGED_IN);
  const email = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  const expiresAt = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT);
  if (loggedIn !== "true" || !email || !expiresAt) {
    return false;
  }
  if (isApiConfigured()) {
    return Boolean(localStorage.getItem(STORAGE_KEYS.ID_TOKEN));
  }
  return true;
}

/**
 * @returns {string|null}
 */
export function getIdToken() {
  return localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
}

/**
 * @returns {string|null}
 */
export function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * @returns {string|null}
 */
export function getRefreshToken() {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * @param {string} key
 * @returns {Date|null}
 */
function readExpiry(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getAccessTokenExpiresAt() {
  return readExpiry(STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT);
}

export function getIdTokenExpiresAt() {
  return readExpiry(STORAGE_KEYS.ID_TOKEN_EXPIRES_AT);
}

/**
 * @param {{ accessToken: string, expiresAt: string, idToken: string, idTokenExpiresAt?: string|null, refreshToken?: string }} tokens
 */
export function storeAuthTokens(tokens) {
  if (tokens.accessToken) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
  }
  if (tokens.expiresAt) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT, tokens.expiresAt);
  }
  if (tokens.idToken) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.idToken);
  }
  if (tokens.idTokenExpiresAt) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN_EXPIRES_AT, tokens.idTokenExpiresAt);
  }
  if (tokens.refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
  }
}

export function clearAuthStorage() {
  for (const key of ALL_SESSION_KEYS) {
    localStorage.removeItem(key);
  }
}

/**
 * Clear clinical mock fixtures from sessionStorage (tenant isolation on logout).
 */
export function purgeMockTenantStorage() {
  try {
    for (const key of Object.values(FIXTURE_KEYS)) {
      sessionStorage.removeItem(`${MOCK_STORAGE_PREFIX}${key}`);
    }
  } catch {
    /* ignore */
  }
}

function abortInFlight() {
  for (const controller of inFlightControllers) {
    try {
      controller.abort();
    } catch {
      /* ignore */
    }
  }
  inFlightControllers.clear();
}

/**
 * @param {{ email: string, name?: string, crfa?: string, role?: string, companyId?: string, userId?: string, status?: string }} opts
 * @param {{ redirect?: boolean }} [options]
 */
export function loginMock(opts, options = {}) {
  const { redirect = true } = options;
  const email = String(opts?.email || "").trim();
  if (!email) {
    throw new Error("email_required");
  }

  const name = String(opts?.name || email.split("@")[0] || "Usuário").trim();
  const crfa = String(opts?.crfa || "CRFa —").trim();
  const role = String(opts?.role || "fono").trim();
  const companyId = String(opts?.companyId || "mock-clinic").trim();
  const userId = String(opts?.userId || `mock-user-${Date.now()}`).trim();
  const status = String(opts?.status || "ativo").trim();
  const expiresAt = new Date(Date.now() + MOCK_TOKEN_EXPIRY_MS).toISOString();

  localStorage.setItem(STORAGE_KEYS.LOGGED_IN, "true");
  localStorage.setItem(STORAGE_KEYS.USER_EMAIL, email);
  localStorage.setItem(STORAGE_KEYS.USER_NAME, name);
  localStorage.setItem(STORAGE_KEYS.USER_CRFA, crfa);
  localStorage.setItem(STORAGE_KEYS.USER_PERMISSION, role);
  localStorage.setItem(STORAGE_KEYS.USER_STATUS, status);
  localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN_EXPIRES_AT, expiresAt);
  localStorage.setItem(STORAGE_KEYS.COMPANY_ID, companyId);

  if (redirect) {
    window.location.href = resolveAppPath(resolveSessionLanding());
  }
}

/**
 * Persist real login response (no billing/plan).
 * @param {object} data - AUTHENTICATED payload.data
 * @param {string} email
 * @param {{ redirect?: boolean }} [options]
 */
export function persistAuthenticatedSession(data, email, options = {}) {
  const { redirect = true } = options;
  const user = data?.user || {};
  const tokens = data?.tokens || {};
  const idToken = tokens.idToken?.value;
  const accessToken = tokens.accessToken?.value;
  const refreshToken = tokens.refreshToken?.value;
  const accessExpires = tokens.accessToken?.expiresAt;
  const idExpires = tokens.idToken?.expiresAt;

  if (!user.companyId || !idToken || !accessToken) {
    const err = new Error("TOKEN_ISSUE_FAILED");
    err.code = "TOKEN_ISSUE_FAILED";
    throw err;
  }

  const status = String(user.status || "ativo").toLowerCase();
  if (status === "inactive" || status === "inativo") {
    const err = new Error("USER_INACTIVE");
    err.code = "USER_INACTIVE";
    throw err;
  }

  purgeMockTenantStorage();
  clearAuthStorage();

  localStorage.setItem(STORAGE_KEYS.LOGGED_IN, "true");
  localStorage.setItem(STORAGE_KEYS.USER_EMAIL, String(email || user.email || "").trim());
  localStorage.setItem(STORAGE_KEYS.USER_NAME, String(user.name || user.email || "").trim());
  localStorage.setItem(STORAGE_KEYS.USER_CRFA, String(user.crfa || "CRFa —").trim());
  localStorage.setItem(
    STORAGE_KEYS.USER_PERMISSION,
    String(user.permission || user.role || "fono").trim(),
  );
  localStorage.setItem(STORAGE_KEYS.USER_STATUS, status === "active" ? "ativo" : status || "ativo");
  localStorage.setItem(STORAGE_KEYS.USER_ID, String(user.userId || "").trim());
  localStorage.setItem(STORAGE_KEYS.COMPANY_ID, String(user.companyId).trim());
  localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, "true");

  storeAuthTokens({
    accessToken,
    expiresAt: accessExpires,
    idToken,
    idTokenExpiresAt: idExpires,
    refreshToken,
  });

  if (redirect) {
    window.location.href = resolveAppPath(resolveSessionLanding());
  }
}

/**
 * @param {{ redirect?: boolean }} [options]
 */
export function logout(options = {}) {
  const { redirect = true } = options;
  abortInFlight();
  clearAuthStorage();
  purgeMockTenantStorage();
  if (redirect) {
    window.location.href = resolveAppPath(CONFIG.LOGIN_PATH);
  }
}

function forceLogoutAndRedirect() {
  logout({ redirect: true });
}

const ensureFreshSession = createEnsureFreshSession({
  getEmail: () => localStorage.getItem(STORAGE_KEYS.USER_EMAIL),
  getRefreshToken,
  getIdTokenExpiresAt,
  getAccessTokenExpiresAt,
  storeAuthTokens,
  getApiBaseUrl,
  getThresholdMinutes: () => CONFIG.AUTH_REFRESH_THRESHOLD_MINUTES,
  getRetryMax: () => CONFIG.AUTH_REFRESH_RETRY_MAX,
  getRetryStorageKey: () => STORAGE_KEYS.AUTH_REFRESH_RETRIES,
  onForceLogout: forceLogoutAndRedirect,
});

/**
 * @returns {Promise<boolean>}
 */
export async function refreshSessionOrLogout() {
  if (!isApiConfigured()) {
    return isAuthenticated();
  }
  if (!localStorage.getItem(STORAGE_KEYS.LOGGED_IN)) {
    return false;
  }
  const result = await ensureFreshSession();
  return Boolean(result?.ok) && isAuthenticated();
}

/**
 * Authenticated fetch using ID token Bearer.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function fetchWithAuth(url, options = {}) {
  if (localStorage.getItem(STORAGE_KEYS.LOGGED_IN) === "true" && isApiConfigured()) {
    await refreshSessionOrLogout();
  }

  const controller = new AbortController();
  inFlightControllers.add(controller);

  const headers = new Headers(options.headers || {});
  const idToken = getIdToken();
  if (idToken) {
    headers.set("Authorization", `Bearer ${idToken}`);
  }

  const merged = {
    ...options,
    headers,
    signal: options.signal || controller.signal,
  };

  try {
    return await fetchWithRetry(fetch, url, merged, {
      maxAttempts: CONFIG.NETWORK_RETRY.MAX_ATTEMPTS,
      baseDelayMs: CONFIG.NETWORK_RETRY.BASE_DELAY_MS,
      retryableStatuses: CONFIG.NETWORK_RETRY.RETRYABLE_STATUSES,
    });
  } finally {
    inFlightControllers.delete(controller);
  }
}

/**
 * Redirects to login when session is missing. Async when API mode needs refresh.
 * @returns {Promise<boolean>}
 */
export async function requireAuth() {
  if (!isAuthenticated()) {
    window.location.href = resolveAppPath(CONFIG.LOGIN_PATH);
    return false;
  }

  if (isApiConfigured()) {
    const ok = await refreshSessionOrLogout();
    if (!ok) {
      window.location.href = resolveAppPath(CONFIG.LOGIN_PATH);
      return false;
    }
  }

  return true;
}

/**
 * @param {string} rootRelative e.g. "pages/painel.html"
 */
export function resolveAppPath(rootRelative) {
  const path = window.location.pathname;
  if (path.includes("/pages/")) {
    const depth = path.split("/pages/")[1].split("/").filter(Boolean).length - 1;
    const prefix = depth > 0 ? "../".repeat(depth) : "";
    return prefix + rootRelative.replace(/^pages\//, "");
  }
  return rootRelative;
}

export function getSessionUser() {
  if (!isAuthenticated()) {
    return null;
  }
  return {
    email: localStorage.getItem(STORAGE_KEYS.USER_EMAIL) || "",
    name: localStorage.getItem(STORAGE_KEYS.USER_NAME) || "",
    crfa: localStorage.getItem(STORAGE_KEYS.USER_CRFA) || "",
    role: localStorage.getItem(STORAGE_KEYS.USER_PERMISSION) || "",
    status: localStorage.getItem(STORAGE_KEYS.USER_STATUS) || "",
    userId: localStorage.getItem(STORAGE_KEYS.USER_ID) || "",
    companyId: localStorage.getItem(STORAGE_KEYS.COMPANY_ID) || "",
  };
}

export { STORAGE_KEYS, isApiConfigured };
